import { BLUE, RED } from "../game/engine.js";

export function cascadePitchRatio(chainIndex) {
  return 1 + Math.log2(Math.max(0, chainIndex) + 1) * 0.45;
}

export class Soundscape {
  constructor() {
    this.enabled = true;
    this.context = null;
    this.master = null;
  }

  async unlock() {
    if (!this.enabled) return;
    if (!this.context) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.2;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === "suspended") await this.context.resume();
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (this.master) this.master.gain.setTargetAtTime(enabled ? 0.2 : 0, this.context.currentTime, 0.02);
  }

  tone(frequency, duration, { type = "sine", volume = 0.2, slide = 1 } = {}) {
    if (!this.enabled || !this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, frequency * slide), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  noise(duration = 0.18, volume = 0.22) {
    if (!this.enabled || !this.context || !this.master) return;
    const sampleCount = Math.ceil(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, sampleCount, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < sampleCount; index += 1) {
      data[index] = (Math.random() * 2 - 1) * (1 - index / sampleCount);
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = buffer;
    filter.type = "lowpass";
    filter.frequency.value = 1200;
    gain.gain.value = volume;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start();
  }

  place(player) {
    const base = player === RED ? 190 : 245;
    this.tone(base, 0.12, { type: "triangle", volume: 0.16, slide: 1.25 });
    window.setTimeout(() => this.tone(base * 1.5, 0.09, { volume: 0.08, slide: 0.95 }), 35);
  }

  explosion(player, chainIndex = 0) {
    const base = player === RED ? 88 : 108;
    const pitchRatio = cascadePitchRatio(chainIndex);
    this.noise(0.2, 0.24);
    this.tone(base * pitchRatio, 0.32, { type: "sawtooth", volume: 0.2, slide: 0.42 });
    this.tone((player === BLUE ? 520 : 430) * pitchRatio, 0.16, { volume: 0.1, slide: 1.3 });
  }

  win(player) {
    const base = player === RED ? 196 : 247;
    [1, 1.25, 1.5, 2].forEach((ratio, index) => {
      window.setTimeout(() => this.tone(base * ratio, 0.42, { type: "triangle", volume: 0.17, slide: 1.04 }), index * 115);
    });
  }
}
